/**
 * Helper: SearchSpring
 * -----------------------------------------------------------------------------
 * Performs SearchSpring API calls
 *
 */
import { shopifyProductByIdQuery } from './shopify'

// eslint-disable-next-line prefer-named-capture-group
const emailRegex = /^([A-Z0-9_+-]+\.?)*[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i

/**
 * Get the currency code
 * @returns {String} Currency code
 */
const getCurrency = () => cnvs?.store?.currency || Shopify?.currency?.active || 'USD'

/**
 *
 * @param {Number} priceCents - The price in cents
 * @returns {Number|String} Price in dollars.cents
 */
const getFormattedPrice = (priceCents = 0) => {
  let price = (priceCents / 100).toFixed(2)

  try {
    price = parseFloat(price)
  } catch (event) {
    // Nothing to do
  }

  return price
}

/**
 * Get cart items in DY format
 * @param {Object} cart - Cart object
 * @returns {Array} Cart items
 */
const getFormattedCart = (cart) => cart.items.map(({
  presentment_price: presentmentPrice,
  price,
  quantity,
  sku: productId,
  variant_options: variantOptions,
}) => {
  return {
    currency: getCurrency(),
    itemPrice: getFormattedPrice(typeof price === 'string' ? presentmentPrice * 100 : price),
    productId,
    quantity,
    size: String(variantOptions[1]),
  }
})

/**
 * Get cart item in DY format
 * @param {Object} item - The cart item
 * @returns {Object} The formatted cart item
 */
const getFormattedItem = (item) => {
  const variantTitleSplit = item.variant_title?.split(' / ') || []
  const sizeFromVariantTitle = variantTitleSplit.length > 1
    ? variantTitleSplit[variantTitleSplit.length - 1]
    : null

  return {
    currency: getCurrency(),
    productId: item.sku,
    quantity: item.quantity,
    size: item.option2
      ? String(item.option2)
      : item.variant_options?.[1] ||
      item.options?.[1]?.value ||
      item.options_with_values?.[1]?.value ||
      item.selectedOptions?.[1]?.value ||
      sizeFromVariantTitle ||
      '',
    value: getFormattedPrice(typeof item.price === 'string' ? item.presentment_price * 100 : item.price),
  }
}

/**
 * Queries the requested SearchSpring API endpoint
 * @param {String} strategyId - The Dynamic Yield strategy ID
 * @returns {Array} Strategy data, including Shopify products
 */
export function getStrategy(strategyId) {
  if (!strategyId) {
    return null
  }

  return new Promise((resolve) => {
    window.DYO?.recommendationWidgetData(strategyId, {}, async(error, data) => {
      if (!data) {
        resolve(null)
      }

      const queue = data?.slots
        ?.map((slot) => {
          if (!slot?.item?.group_id) {
            return false
          }

          return shopifyProductByIdQuery(
            `gid://shopify/Product/${slot.item.group_id}`,
            300000,
          )
        })
        ?.filter(Boolean)

      const shopifyProducts = await Promise.all(queue)

      resolve({
        data,
        products: shopifyProducts.filter(Boolean) || null,
      })
    })
  })
}

/**
 * Takes in DY and Shopify carousel settings and merges them
 * into a final settings object.
 * @param {Object} dySettings - The settings from DY
 * @param {Object} shopifySettings - The settings from Shopify
 * @returns {Object} The merged settings
 */
export function getProductCarouselSettings(dySettings, shopifySettings) {
  let gap
  try {
    gap = parseInt(dySettings?.gap) || shopifySettings?.gap || 1
  } catch (event) {
    gap = shopifySettings?.gap || 1
  }

  return {
    successMessage: dySettings.showSuccessMessage === 'true'
      ? dySettings.successMessage
      : false,
    title:
      dySettings?.title || shopifySettings?.title || '',
    titleAlignment:
      dySettings?.titleAlignment || shopifySettings?.titleAlignment || 'default',
    cardContentAlignment:
      dySettings?.cardContentAlignment || '',
    cardBackground:
      dySettings?.cardBackground || shopifySettings?.cardBackground || '--color-brand-white',
    cardLayout:
      dySettings?.cardLayout || shopifySettings?.cardLayout || 'regular',
    centerInsufficientSlides:
      dySettings?.centerInsufficientSlides
        ? dySettings?.centerInsufficientSlides === 'true'
        : shopifySettings?.centerInsufficientSlides || false,
    columnCount:
      dySettings?.columnCount || shopifySettings?.columnCount || 'small',
    gap,
    hideAllInfo: dySettings?.hideAllInfo
      ? dySettings?.hideAllInfo === 'true'
      : shopifySettings?.hideAllInfo || false,
    paddedImages: dySettings?.paddedImages
      ? dySettings?.paddedImages === 'true'
      : true,
    showSwatches: dySettings?.showSwatches
      ? dySettings?.showSwatches === 'true'
      : shopifySettings?.showSwatches || true,
    showBadges: dySettings?.showBadges
      ? dySettings?.showBadges === 'true'
      : shopifySettings?.showBadges || true,
    showCarouselArrows: dySettings?.showCarouselArrows
      ? dySettings?.showCarouselArrows === 'true'
      : shopifySettings?.showCarouselArrows || true,
    showFinalSale: dySettings?.showFinalSale
      ? dySettings?.showFinalSale === 'true'
      : shopifySettings?.showFinalSale || true,
    showPreorderBadge: dySettings?.showPreorderBadge
      ? dySettings?.showPreorderBadge === 'true'
      : shopifySettings?.showPreorderBadge || true,
    showPricing: dySettings?.showPricing
      ? dySettings?.showPricing === 'true'
      : true,
    showSecondaryName: dySettings?.showSecondaryName
      ? dySettings?.showSecondaryName === 'true'
      : shopifySettings?.showSecondaryName || true,
    showFooterButton: dySettings?.showBottomButton
      ? dySettings?.showBottomButton === 'true'
      : shopifySettings?.showBottomButton || true,
    footerButtonLabel: dySettings?.footerButtonLabel ||
      shopifySettings?.footerButtonLabel ||
      '',
    footerButtonUrl: dySettings?.footerButtonUrl ||
      shopifySettings?.footerButtonUrl ||
      '',
    isDickeyBundle: dySettings?.isDickeyBundle
      ? dySettings?.isDickeyBundle === 'true'
      : shopifySettings?.isDickeyBundle || false,
    showWishlist: dySettings?.showWishlist
      ? dySettings?.showWishlist === 'true'
      : shopifySettings?.showWishlist || true,
    showQuickAdd: dySettings?.showQuickAdd
      ? dySettings?.showQuickAdd === 'true'
      : shopifySettings?.showQuickAdd || true,
    subtitle: dySettings?.subtitle || '',
    template: dySettings?.template || null,
    titleClass: dySettings?.titleSizeDesktop && dySettings?.titleSizeMobile
      ? `${dySettings?.titleSizeDesktop} ${dySettings?.titleSizeMobile}`
      : shopifySettings?.titleClass || 'text-setting-heading-desktop-s text-setting-heading-mobile-s',
    showFinalImage: dySettings?.showFinalImage
      ? dySettings?.showFinalImage === 'true'
      : shopifySettings?.showFinalImage || true,
    finalImageOverlay: dySettings?.finalImageOverlay
      ? dySettings?.finalImageOverlay === 'true'
      : shopifySettings?.finalImageOverlay || true,
    finalImageDesktop:
      dySettings?.finalImageDesktop || shopifySettings?.finalImageDesktop || '',
    finalImageMobile:
      dySettings?.finalImageMobile || shopifySettings?.finalImageMobile || '',
    finalImageText:
      dySettings?.finalImageText || shopifySettings?.finalImageText || '',
    finalImageUrl:
      dySettings?.finalImageUrl || shopifySettings?.finalImageUrl || '',
    zeroSearchDrawerRecommendation: shopifySettings?.zeroSearchDrawerRecommendation || false,
    backInStockDrawerRecommendation: shopifySettings?.backInStockDrawerRecommendation || false,
  }
}

/**
 * Sends a DY purchase event.
 * @param {String} transactionId - Unique transaction ID
 * @param {Array} cart - Cart items array
 * @param {Number} value - Cart total amount
 * @param {String} currency - Order currency
 */
export const purchase = (transactionId, cart, value, currency) => {
  window.DY?.API('event', {
    name: 'Purchase',
    properties: {
      cart,
      currency,
      dyType: 'purchase-v1',
      uniqueTransactionId: transactionId,
      value,
    },
  })
}

/**
 * Sends a DY add to cart event.
 * @param {Object} item - The item that was added
 * @param {Object} cart - Cart object
 */
export const addToCart = (item, cart) => {
  if (!item || !cart) {
    return
  }

  window.DY?.API('event', {
    name: 'Add to Cart',
    properties: {
      dyType: 'add-to-cart-v1',
      cart: getFormattedCart(cart),
      // TODO: Uncomment when Shopify response amount is fixed
      currency: 'USD',
      // currency: getCurrency(),
      ...getFormattedItem(item),
    },
  })
}

/**
 * Sends a special DY add to cart event for the PDP.
 */
export const addToCartFromPDP = () => {
  window.DY?.API('event', {
    name: 'PDP_Main_ATC',
    properties: {
      currency: getCurrency(),
    },
  })
}

/**
 * Sends a special DY click event for the homepage banner.
 */
export const homepageBannerCTR = () => {
  window.DY?.API('event', {
    name: 'hp_banner_click',
    properties: {
      currency: getCurrency(),
    },
  })
}

/**
 * Sends a DY remove from cart event.
 * @param {Object} item - The item that was removed
 * @param {Object} cart - Cart object
 */
export const removeFromCart = (item, cart) => {
  if (!item || !cart) {
    return
  }

  window.DY?.API('event', {
    name: 'Remove from Cart',
    properties: {
      dyType: 'remove-from-cart-v1',
      cart: getFormattedCart(cart),
      // TODO: Uncomment when Shopify response amount is fixed
      currency: 'USD',
      // currency: getCurrency(),
      ...getFormattedItem(item),
    },
  })
}

/**
 * Sends a DY sync cart event.
 * @param {Object} cart - Cart object
 */
export const syncCart = (cart) => {
  if (!cart) {
    return
  }

  window.DY?.API('event', {
    name: 'Synchronize Cart',
    properties: {
      dyType: 'sync-cart-v1',
      cart: getFormattedCart(cart),
      currency: getCurrency(),
      value: getFormattedPrice(cart.total_price),
    },
  })
}

/**
 * Sends a DY add to wishlist event.
 * @param {Object} item - Cart item
 */
export const addToWishlist = (item) => {
  if (!item) {
    return
  }

  const formattedItem = getFormattedItem(item)
  window.DY?.API('event', {
    name: 'Add to Wishlist',
    properties: {
      dyType: 'add-to-wishlist-v1',
      productId: formattedItem.productId,
      size: formattedItem.size,
    },
  })
}

/**
 * Sends a DY identify event.
 * @param {Number} email - User email
 */
export const identify = (email) => {
  if (!cnvs.customer?.id || !window.DYO) {
    return
  }

  const properties = {
    dyType: 'identify-v1',
    cuid: cnvs?.customer?.id,
    cuidType: 'Shopify ID',
  }

  if (email) {
    properties.hashedEmail = window.DYO.dyhash.sha256(email.toLowerCase())
  }

  // Identify DY user on page load if customer logged in
  window.DY?.API('event', {
    name: 'Identify',
    properties,
  })
}

/**
 * Sends a DY keyword search event
 * @param {String} query - The search query
 */
export const keywordSearch = (query) => {
  if (!window.DYO) {
    return
  }

  // Identify DY user on page load if customer logged in
  window.DY?.API('event', {
    name: 'Keyword Search',
    properties: {
      dyType: 'keyword-search-v1',
      keywords: query,
    },
  })
}

/**
 * Sends a DY login event.
 * @param {Number} email - User email
 */
export const login = (email) => {
  if (!cnvs.customer?.id || !window.DYO) {
    return
  }

  const properties = {
    dyType: 'login-v1',
    cuid: cnvs?.customer?.id,
    cuidType: 'Shopify ID',
  }

  if (email) {
    properties.hashedEmail = window.DYO.dyhash.sha256(email.toLowerCase())
  }

  // Identify DY user on page load if customer logged in
  window.DY?.API('event', {
    name: 'Login',
    properties,
  })
}

/**
 * Sends a DY newsletter subscription event,
 * as well as a user identification event.
 * @param {String} email - Subscribed email
 */
export const newsletterSubscription = (email) => {
  if (!emailRegex.test(email)) {
    return
  }

  window.DY?.API('event', {
    name: 'Newsletter Subscription',
    properties: {
      dyType: 'newsletter-subscription-v1',
      cuid: cnvs?.customer?.id,
      cuidType: 'Shopify ID',
      hashedEmail: window.DYO.dyhash.sha256(email.toLowerCase()),
    },
  })

  identify(email)
}

/**
 * Sends a DY filter items event.
 * @param {Object} ssFilter - The Searchspring filter
 */
export const filterItems = (ssFilter = {}) => {
  if (!ssFilter) {
    return
  }

  window.DY?.API('event', {
    name: 'Filter Items',
    properties: {
      dyType: 'filter-items-v1',
      filterType: ssFilter.filterLabel,
      filterStringValue: ssFilter.filterValue,
    },
  })
}

/**
 * Sends a DY sort items event.
 * @param {Object} ssSortOption - The Searchspring sort option
 */
export const sortItems = (ssSortOption = {}) => {
  if (!ssSortOption || !ssSortOption.active) {
    return
  }

  window.DY?.API('event', {
    name: 'Sort Items',
    properties: {
      dyType: 'sort-items-v1',
      sortBy: ssSortOption.label,
      sortOrder: ssSortOption.direction === 'desc' ? 'DESC' : 'ASC',
    },
  })
}

/**
 * Sends a DY watch video event.
 * @param {String} videoId - Video ID
 * @param {Boolean} isEnded - Video ended
 * @param {Boolean} isAutoplay - Is autoplay
 * @param {Number} progress - Progress out of 100
 */
export const watchVideo = (videoId, isEnded = false, isAutoplay = false, progress = 0) => {
  window.DY?.API('event', {
    name: 'Video Watch',
    properties: {
      dyType: 'video-watch-v1',
      autoplay: isAutoplay,
      itemId: videoId,
      progress: isEnded ? 'VIDEO_ENDED' : 'VIDEO_PROGRESS',
      progressPercent: progress,
    },
  })
}

/**
 * Sends a DY custom scroll depth event.
 * @param {Number} depthPercentage - Depth out of 100
 */
export const scrollDepth = (depthPercentage = 0) => {
  window.DY?.API('event', {
    name: 'Scroll Depth',
    properties: {
      depthPercentage,
    },
  })
}

/**
 * Build loading products.
 * @param {Number} [count] - Number of products.
 * @returns {Array}
 */
export function getLoadingProducts(count = 6) {
  return Array.from(Array(count).keys()).map((index) => {
    return {
      id: `loading-product-${index}`,
      images: [],
      index,
      loading: true,
      price: false,
      secondaryImage: {},
    }
  })
}
